import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import { type ReactNode, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useEmail from '~/hooks/useEmail';
import useToast from '~/hooks/useToast';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';

import DataLayer from '~/services/DataLayer';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';
import ClientSideInputs from './_components/ClientSideInputs';
import ServerSideInputs from './_components/ServerSideInputs';
import useSignupSubmit from './_hooks/useSignupSubmit';

import { usePageContext } from '~/context/usePageContext';
import type { RootLoaderData } from '~/root';

type RenderProps = {
  setEmail: (email: string) => void;
  isLoading: boolean;
};

type SignupActionProps = {
  id: string;
  signupFlowId?: string;
  automationIds?: string; // Ex: `1,2,3` (not an array)
  premiumOfferId?: string;
  redirectUrl?: string;
  children: (props: RenderProps) => ReactNode;
  onSuccess?: () => void;
};

const SignupAction = ({
  id,
  signupFlowId,
  automationIds,
  premiumOfferId,
  redirectUrl,
  children,
  onSuccess,
}: SignupActionProps) => {
  const { handleCloseModal, isModalOpen, currentModal } = useGlobalModals();
  const fallbackId = useId();
  const [, setEmail] = useEmail();
  const fetcher = useComponentFetcher();
  const { t } = useTranslation();
  const { publication, site, isPostsPage } = useRouteLoaderData('root') as RootLoaderData;
  const shouldConsentToTOS = !!site.viewable_site_version.settings.terms_of_service_enabled;
  const { cookies, visitToken, referralId, setCookie, currentPath } = usePageContext();
  const [isLoading, setIsLoading] = useState(false);
  const { startRedirects } = useSignupRedirects();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const offerId = premiumOfferId || searchParams.get('offer_id') || undefined;
  const formId = id || fallbackId;

  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);

  const { double_opt_required: isDoubleOptRequired, has_referral_program: isReferralProgram } = publication;

  const shouldDoubleOpt = Boolean(isDoubleOptRequired || (isReferralProgram && referralId));

  const { handleSubmit } = useSignupSubmit({
    formId,
    shouldConsentToTOS,
    onError: () => {
      setIsLoading(false);
    },
    fetcher,
  });

  const getToastMessage = () => {
    if (publication.require_subscriber_approval) {
      return t('notifications.subscribe.require_approval');
    }

    if (publication.double_opt_required) {
      return t('notifications.subscribe.confirm');
    }

    return t('notifications.subscribe.subscribed');
  };

  const handleSuccess = (data: any) => {
    window._pxFormId = undefined;

    setIsLoading(false);
    setCookie({ name: 'email_capture_disabled', value: 'true', days: 30 });
    formRef.current?.reset();

    if (isModalOpen) {
      handleCloseModal({ preventScrollReset: true });
    }

    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    if (data?.signupFlow) {
      // Don't trigger signup flow on post page when signup is triggered
      // via email capture or content gate
      // as the user should stay on the page to continue reading
      if (isPostsPage && currentModal && ['email_capture', 'content_gate'].includes(currentModal)) {
        return;
      }

      startRedirects(data.signupFlow, data.signupFlow.steps[0]);
      return;
    }

    if (data?.redirectTo) {
      window.location.href = data.redirectTo;
      return;
    }

    onSuccess?.();
  };

  const handleError = (_data: any) => {
    window._pxFormId = undefined;
    setIsLoading(false);
  };

  useToast(fetcher?.data?.toast);

  return (
    <ActionForm
      id={formId}
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/create"
      onSubmit={(e) => {
        setIsLoading(true);
        handleSubmit(e);
      }}
      onSuccess={handleSuccess}
      onError={handleError}
    >
      <StatusInputs name="subscribe" successMessage={getToastMessage()} errorMessage={t('errors.default')} />
      <ClientSideInputs
        premiumOfferId={offerId}
        visitToken={visitToken}
        referralId={referralId}
        boostAffiliateRecommendingPublicationId={boostAffiliateRecommendingPublicationId}
        fallbackPath={currentPath}
        redirectUrl={redirectUrl}
      />
      <ServerSideInputs shouldDoubleOpt={shouldDoubleOpt} signupFlowId={signupFlowId} automationIds={automationIds} />
      {children({ isLoading, setEmail })}
    </ActionForm>
  );
};

export default SignupAction;
